import { Box, Button, Step, StepLabel, Stepper } from "@mui/material";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { gateway } from "../gateway";
import { resetCreatePlanState } from "../redux/features/createPlanStateSlice";
import { LoaderComponent } from "./Loader";
import { BaseDialog } from "./common/BaseDialog";
import { CreatePlanReview } from "./createPlanReview";
import { PlanBenefits } from "./planBenefits";
import { PlanPriceDetails } from "./planPriceDetails";
import { ProductDetails } from "./productDetails";

const styles = {
  titleSx: {
    fontWeight: "bold",
  },
  contentSx: { textAlign: "center", color: "var(--fx-surface-text, #0F172A)" },
  actionSx: {
    justifyContent: "center",
  },
  btnCloseSx: {
    backgroundColor: "grey",
    color: "white",
    "&:hover": { backgroundColor: "var(--fx-surface-sidebar, #233044)" },
    margin: "0 8px",
  },
};

export const CreatePlan = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set());
  const [isFormValid, setIsFormValid] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [planName, setPlanName] = useState("");

  const steps = ["Plan Details", "Pricing", "Benefits"];
  const newPlanState = useSelector((state) => state.createPlanState);

  const token = sessionStorage.getItem("token");

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    navigate("/business");
  };

  const handleOpenDialog = () => {
    setOpen(false);
    setOpenDialog(true);
  };

  const createNewPlan = async () => {
    try {
      setOpen(true);
      setPlanName(newPlanState.planName);
      const productData = {
        name: newPlanState.planName,
        description: newPlanState.description,
        type: newPlanState.type,
        category: newPlanState.category,
        benefits: [newPlanState.coverageBenefit, newPlanState.connectionBenefit],
      };

      const productResponse = await gateway.post("/paypal/products", productData, {
        headers: {
          Authorization: `${token}`,
        },
      });

      const newProduct = productResponse.data.data;
      const planPrice = formatCurrency(newPlanState.price);


      let billingCycles = [];
      if (newPlanState.trial) {
        const priceTrail = formatCurrency(newPlanState.priceTrial);
        billingCycles.push({
          frequency: {
            interval_unit: newPlanState.frequencyTrial,
            interval_count: newPlanState.intervalCountTrial,
          },
          tenure_type: newPlanState.ternureTypeTrial,
          sequence: newPlanState.sequenceTrail,
          total_cycles: newPlanState.totalCyclesTrial,
          pricing_scheme: {
            fixed_price: {
              value: priceTrail,
              currency_code: newPlanState.currency,
            },
          },
        });
      }

      billingCycles.push({
        frequency: {
          interval_unit: newPlanState.billingFrequency,
          interval_count: newPlanState.intervalCount,
        },
        tenure_type: newPlanState.ternureType,
        sequence: newPlanState.sequence,
        total_cycles: newPlanState.totalCycles,
        pricing_scheme: {
          fixed_price: {
            value: planPrice,
            currency_code: newPlanState.currency,
          },
        },
      });
      const planData = {
        product_id: newProduct.paypalId,
        status: newPlanState.status,
        billing_cycles: billingCycles,
        payment_preferences: {
          auto_bill_outstanding: true,
          setup_fee: {
            value: "0",
            currency_code: newPlanState.currency,
          },
          setup_fee_failure_action: "CANCEL",
          payment_failure_threshold: 3,
        },
        taxes: {
          percentage: "0",
          inclusive: "false",
        },
      };

      const planResponse = await gateway.post("/paypal/plans", planData, {
        headers: {
          Authorization: `${token}`,
        },
      });
      if (planResponse.status === 201) {
        console.log("Plan created successfully");
        handleOpenDialog();
        dispatch(resetCreatePlanState());
      }

    } catch (error) {
      console.log(error);
      setOpen(false);
    }
  };

  function formatCurrency(value) {
    let formattedValue = Number(value).toFixed(2);
    let [integer, decimal] = formattedValue.split(".");
    integer = integer.padStart(2, "0");
    return `${integer}.${decimal}`;
  }

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return <ProductDetails setIsFormValid={setIsFormValid}></ProductDetails>;
      case 1:
        return <PlanPriceDetails setIsFormValid={setIsFormValid}></PlanPriceDetails>;
      case 2:
        return <PlanBenefits setIsFormValid={setIsFormValid}></PlanBenefits>;
      default:
        return <CreatePlanReview></CreatePlanReview>;
    }
  };
  return (
    <Box
      sx={{
        width: { xs: "100%", sm: "90%", lg: "70%" },
        padding: 2,
        ml: -2,
        height: "auto",
      }}
    >
      <Stepper activeStep={activeStep} sx={{ mt: 2 }}>
        {steps.map((label, index) => {
          const stepProps = {};
          const labelProps = {};
          if (isStepSkipped(index)) {
            stepProps.completed = false;
          }
          return (
            <Step key={label} {...stepProps}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 5,
        }}
      >
        {renderStepContent(activeStep)}
      </Box>

      {activeStep === steps.length ? (
        <React.Fragment>
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            <Button color="inherit" disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
              Back
            </Button>
            <Box sx={{ flex: "1 1 auto" }} />
            <Button onClick={createNewPlan}>Create Plan</Button>
          </Box>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            <Button color="inherit" disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
              Back
            </Button>
            <Box sx={{ flex: "1 1 auto" }} />

            <Button onClick={handleNext} disabled={!isFormValid}>
              {activeStep === steps.length - 1 ? "Review Plan" : "Next"}
            </Button>
          </Box>
        </React.Fragment>
      )}
      <LoaderComponent open={open}></LoaderComponent>
      <BaseDialog
        openState={openDialog}
        setOpenState={handleCloseDialog}
        disableScape={true}
        title={`PlAN CREATED SUCCESSFULLY`}
        titleSx={styles.titleConnectionSx}
        content={`New plan "${planName}" is available`}
        contentSx={styles.contentSx}
        actionSx={styles.actionSx}
        actions={[
          {
            label: "Return Home",
            onClick: () => {
              handleCloseDialog();
            },
            style: styles.btnCloseSx,
            autoFocus: true,
          },
        ]}
      ></BaseDialog>
    </Box>
  );
};
